import * as Sentry from '@sentry/react'

type LogArgs = Parameters<typeof console.log>
type ConsoleMethod = 'log' | 'warn' | 'error'

const sentryLogLevelMap: Record<ConsoleMethod, 'info' | 'warn' | 'error'> = {
  log: 'info',
  warn: 'warn',
  error: 'error',
}

const formatMessage = (args: LogArgs): string =>
  args
    .map((arg) => {
      if (typeof arg === 'string') return arg
      if (typeof arg === 'number' || typeof arg === 'boolean' || typeof arg === 'bigint') {
        return String(arg)
      }
      if (arg === null) return 'null'
      if (arg === undefined) return 'undefined'

      try {
        return JSON.stringify(arg)
      } catch (error) {
        return `[unserializable: ${(error as Error).message || 'unknown error'}]`
      }
    })
    .join(' ')

const sentryLogger = Sentry.logger as
  | {
      info?: (message: string) => void
      warn?: (message: string) => void
      error?: (message: string) => void
    }
  | undefined

const emitLog = (type: ConsoleMethod, args: LogArgs) => {
  console[type](...args)

  const sentryMethod = sentryLogger?.[sentryLogLevelMap[type]]
  if (!sentryMethod) return

  const message = formatMessage(args)

  try {
    sentryMethod(message)
  } catch {
    // Ignore logging errors so we never block console semantics
  }
}

export const Logger = {
  log: (...args: LogArgs) => emitLog('log', args),
  warn: (...args: LogArgs) => emitLog('warn', args),
  error: (...args: LogArgs) => emitLog('error', args),
}

export default Logger
